import { FacilityRoles } from "@src/appV2/FacilityUser/types";
import { z } from "zod";

import { UserType } from "../ShiftBlock/types/user";

// eslint-disable-next-line no-restricted-syntax
export enum UrgencyType {
  NCNS = 1,
  LATE_CANCELLATION = 2,
  LAST_MINUTE = 3,
}

// eslint-disable-next-line no-restricted-syntax
export enum ClockCheckType {
  LOCATION = "LOCATION",
  NFC = "NFC",
  MANUAL = "MANUAL",
  INTEGRATION = "INTEGRATION",
}

export const clockInOutCheckTypeSchema = z.nativeEnum(ClockCheckType);

export const TIME_SLOTS = ["am", "pm", "noc"] as const;
export type TimeSlot = typeof TIME_SLOTS[number];

export function isTimeSlot(timeSlotString: string): timeSlotString is TimeSlot {
  return (TIME_SLOTS as readonly string[]).includes(timeSlotString);
}

export interface SetShiftUrgentPayload {
  shiftId: string;
  urgency?: UrgencyType;
  urgencyReason?: string;
  agentId?: string;
  extendBookabilityTo?: string;
}

export const setShiftUrgentRequestSchema = z.object({
  shiftId: z.string(),
  urgency: z.nativeEnum(UrgencyType).optional(),
  urgencyReason: z.string().optional(),
  agentId: z.string().optional(),
  extendBookabilityTo: z.string().optional(),
});

export const setShiftUrgentResponseSchema = z.union([
  z.object({ error: z.string() }),
  z.object({ setShiftUrgent: z.boolean() }),
]);

export type SetShiftUrgentApiRequestType = z.infer<typeof setShiftUrgentRequestSchema>;
export type SetShiftUrgentApiResponseType = z.infer<typeof setShiftUrgentResponseSchema>;

export const getFacilityDayShiftsParamsSchema = z.object({
  shiftId: z.string().optional(),
  dateFilter: z
    .object({
      start: z.string(),
      end: z.string(),
    })
    .optional(),
  tmz: z.string().optional(),
  type: z.string().optional(),
  facilityId: z.string().optional(),
  includeNegotiations: z.boolean().optional(),
  lastRefreshedDateCellAt: z.string().optional(),
  facilityShiftsCounterForDay: z.number().optional(),
  includeWorkerLeftEarlyRequests: z.boolean().optional(),
});

const chargeBreakDownSchema = z.object({
  baseCharge: z.number().nullish(),
  time: z.number().nullish(),
  rush: z.number().nullish(),
  holiday: z.number().nullish(),
  manual: z.number().nullish(),
  negotiatedCharge: z.number().nullish(),
  maxChargeCredit: z.number().nullish(),
});

const nurseChargeBreakDownSchema = z.object({
  LVN: chargeBreakDownSchema,
  RN: chargeBreakDownSchema,
});

export type ChargeBreakDown = z.infer<typeof chargeBreakDownSchema>;

export type NurseChargeBreakDown = z.infer<typeof nurseChargeBreakDownSchema>;

export const computedArrivalSchema = z.object({
  workerId: z.string(),
  bookedAt: z.string().datetime(),
  shiftStartAt: z.string().datetime(),
  targetArrivalTime: z.string().datetime(),
  gracePeriodInMins: z.number(),
  durationInMinutes: z.number(),
  distanceInMeters: z.number(),
  provider: z.string(),
});

const shiftSchema = z.object({
  _id: z.string(),
  name: z.string().optional(),
  start: z.string(),
  end: z.string(),
  deleted: z.boolean().optional(),
  createdAt: z.string().datetime().optional(),
  urgency: z.nativeEnum(UrgencyType).optional(),
  urgencyReason: z.string().optional(),
  attendance: z
    .object({
      isWorkerFarFromFacility: z.boolean().optional(),
      arrivedFacilityGeofenceAt: z.coerce.date().optional(),
      leftFacilityGeofenceAt: z.coerce.date().optional(),
      leftFacilityGeofence: z.boolean().optional(),
    })
    .optional(),
  agent: z
    .object({
      name: z.string().optional(),
      userId: z.string().optional(),
    })
    .optional(),
  clockInOut: z
    .object({
      start: z.string().datetime().optional(),
      end: z.string().datetime().optional(),
      clockInBy: z.string().optional(),
      clockOutBy: z.string().optional(),
    })
    .optional(),
  lunchInOut: z
    .object({
      start: z.string().datetime().nullish().optional(),
      end: z.string().datetime().nullish().optional(),
      lunchOutBy: z.string().nullish().optional(),
      lunchInBy: z.string().nullish().optional(),
    })
    .optional(),
  postedBy: z
    .object({
      userId: z.string().optional(),
      name: z.string().optional(),
      userType: z.nativeEnum(UserType).optional(),
    })
    .optional(),
  agentReq: z.string(),
  replacedShiftId: z.string().nullable().optional(),
  workerLeftEarly: z
    .object({
      id: z.string(),
      leftWithPermission: z.boolean(),
      replacementRequested: z.boolean(),
    })
    .optional(),
  charge: z.number().optional(),
  chargeBreakDown: chargeBreakDownSchema.optional(),
  nurseChargeBreakDown: nurseChargeBreakDownSchema.optional(),
  computedArrivals: z.array(computedArrivalSchema).optional(),
  bookedAt: z.string().datetime().optional(),
  orientationBlockId: z.string().optional(),
});

export type ComputedArrival = z.infer<typeof computedArrivalSchema>;
export type Shift = z.infer<typeof shiftSchema>;

export const shiftAdjustmentSchema = z.object({
  pay: z.number().optional(),
  time: z.number().optional(),
  timeLogs: z
    .object({
      clockIn: z.string().datetime().optional(),
      clockOut: z.string().datetime().optional(),
      lunchIn: z.string().datetime().optional(),
      lunchOut: z.string().datetime().optional(),
      skippedLunch: z.boolean().optional(),
    })
    .optional(),
  createdAt: z.string().datetime(),
});

export const shiftWithAdjustmentSchema = shiftSchema.merge(
  z.object({ shiftAdjustments: z.array(shiftAdjustmentSchema).optional() })
);

export const facilityShiftSchema = shiftWithAdjustmentSchema.merge(
  z.object({
    durationWithoutBreaksInHours: z.number(),
    clockInOut: z
      .object({
        start: z.string().datetime().optional(),
        end: z.string().datetime().optional(),
        clockInBy: z.string().optional(),
        clockInCheck: clockInOutCheckTypeSchema.optional(),
        clockOutBy: z.string().optional(),
        clockOutCheck: clockInOutCheckTypeSchema.optional(),
      })
      .optional(),
    timecard: z
      .object({
        status: z.string().nullable().optional(),
        digitallySignedBy: z.string().nullable().optional(),
        uploadedBy: z.string().nullable().optional(),
        createdAt: z.coerce.date().optional(),
        updatedAt: z.coerce.date().optional(),
        files: z
          .array(
            z.object({
              fileStorageFileKey: z.string().optional(),
              filename: z.string(),
              timesheet: z.string().optional(),
              signedType: z.string().optional(),
              url: z.string().optional(),
            })
          )
          .nullish(),
      })
      .nullish(),
    postedBy: shiftSchema.shape.postedBy
      .unwrap()
      .extend({
        roles: z.array(z.nativeEnum(FacilityRoles)).optional(),
      })
      .optional(),
    facility: z
      .object({
        name: z.string(),
        tmz: z.string(),
        userId: z.string(),
      })
      .optional(),
  })
);

export type ShiftAdjustment = z.infer<typeof shiftAdjustmentSchema>;
export type ShiftWithAdjustments = z.infer<typeof shiftWithAdjustmentSchema>;
export type FacilityShift = z.infer<typeof facilityShiftSchema>;

export const getFacilityDayShiftsResponseSchema = z.array(shiftWithAdjustmentSchema);

export const getFacilityDayShiftsRequestSchema = getFacilityDayShiftsParamsSchema;

export type GetFacilityDayShiftsParams = z.infer<typeof getFacilityDayShiftsParamsSchema>;
export type GetFacilityDayShiftsApiResponseType = z.infer<
  typeof getFacilityDayShiftsResponseSchema
>;

export const getFacilityShiftsRequestSchema = z.object({
  shiftId: z.string().optional(),
  facilityId: z.string().optional(),
});

export const getFacilityShiftsResponseSchema = z.array(facilityShiftSchema);

export type GetFacilityShiftsApiResponseType = z.infer<typeof getFacilityShiftsResponseSchema>;

// eslint-disable-next-line no-restricted-syntax
export enum ShiftSpecificationCategory {
  GRADE_AND_SUBJECT = "GRADE_AND_SUBJECT",
  AGE_GROUP = "AGE_GROUP",
  PATIENT_SCHEDULE = "PATIENT_SCHEDULE",
  DRIVE_ROUTE = "DRIVE_ROUTE",
  ADDITIONAL_INFO = "ADDITIONAL_INFO",
}
